import React, {PropsWithChildren, useMemo, useState} from 'react';
import {Box, styled, Typography} from '@mui/material';
import {capitalize} from 'lodash';
import {useDjangoContext} from './useAuthContext';
import {useDebugContext} from './DebugProvider';
import {themeColors} from '../theme/Theme';

import {
  GlobalPermissionEnum,
  PermGroupsEnum,
  UserPermissionEnum,
} from '../schema-types/globalTypes';
import {GetLoginInfo_getCurrentUser} from '../schema-types/GetLoginInfo';

export const hasGroup = (
  user: GetLoginInfo_getCurrentUser | null,
  group: PermGroupsEnum
) => {
  return !!user?.groups?.find((x) => x?.name === group);
};
export const hasPermission = (
  user: GetLoginInfo_getCurrentUser | null,
  features: (GlobalPermissionEnum | UserPermissionEnum)[]
) => {
  const hasGroupPerm = !!user?.perms?.find(
    (x) => x?.codename && features.includes(x.codename)
  );
  const hasUserPerm = !!user?.userPerms?.find(
    (x) => x?.codename && features.includes(x.codename)
  );
  return hasGroupPerm || hasUserPerm;
};

export const useHasPermission = (
  user: GetLoginInfo_getCurrentUser | null,
  features: (GlobalPermissionEnum | UserPermissionEnum)[]
) => {
  return useMemo(() => {
    if (!user) {
      return false;
    }
    return hasPermission(user, features);
  }, [user, features]);
};

export const useFeaturePermission = (
  features: (GlobalPermissionEnum | UserPermissionEnum)[]
) => {
  const {djangoUser} = useDjangoContext();
  return useMemo(() => {
    if (!djangoUser) {
      return false;
    }
    return hasPermission(djangoUser, features);
  }, [djangoUser, features]);
};
export const FeatureBoundary: React.FC<
  PropsWithChildren<{
    features: (GlobalPermissionEnum | UserPermissionEnum)[];
    showErrorIfDenied: boolean;
  }>
> = ({features, children, showErrorIfDenied}) => {
  const {djangoUser} = useDjangoContext();
  const allowed = hasPermission(djangoUser, features);
  const {debug} = useDebugContext();

  const notVisibleContent = showErrorIfDenied ? (
    <Box
      data-test="Error message display"
      width="100%"
      p={2}
      display="flex"
      justifyContent="center"
    >
      <Typography>
        User does not have permission:{' '}
        {features.map((x) =>
          capitalize(
            x
              .replaceAll('_', ' ')
              .toLocaleLowerCase()
              .replace('can access ', '')
              .replace(/\bfe\b/, '')
          )
        )}
      </Typography>
    </Box>
  ) : null;

  const notVisibleDisplay = debug?.permissions ? (
    <div style={{position: 'relative', opacity: 0.3}}>{children}</div>
  ) : (
    // children
    notVisibleContent
  );

  if (allowed) {
    return (
      <PermissionBoundaryWrapper bigDisplay={showErrorIfDenied} featureNames={features}>
        {children}
      </PermissionBoundaryWrapper>
    );
  }

  return (
    <PermissionBoundaryWrapper bigDisplay={showErrorIfDenied} featureNames={features}>
      {notVisibleDisplay}
    </PermissionBoundaryWrapper>
  );
};

const Overlay = styled(Box)`
  position: absolute;
  z-index: 900;
  top: -1px;
  right: -1px;
  bottom: -1px;
  left: -1px;
  opacity: 0.4;
  background-color: grey;
`;

export const PermissionBoundaryWrapper: React.FC<
  PropsWithChildren<{
    featureNames: (GlobalPermissionEnum | UserPermissionEnum)[];
    bigDisplay: boolean;
  }>
> = ({featureNames, children, bigDisplay}) => {
  const {debug} = useDebugContext();
  const [showOverlay, setShowOverlay] = useState(false);
  if (!debug?.permissions) {
    return <>{children}</>;
  }
  // const displayText = `${allowed.join(' & ')} ${bigDisplay ? ' can view' : ''}`;

  // const firstColor = allowed.length ? colors[allowed[0]] : themeColors.grey;
  // const secondColor = allowed.length > 1 ? colors[allowed[1]] : themeColors.grey;
  return (
    <Box position="relative">
      <Box
        position="absolute"
        zIndex={1000}
        top={-1}
        right={-1}
        display="flex"
        onMouseEnter={() => setShowOverlay(true)}
        onMouseLeave={() => setShowOverlay(false)}
      >
        {featureNames.map((permissionsGroup) => {
          // const color = colors[permissionsGroup];
          return (
            <Box
              key={permissionsGroup}
              fontSize={bigDisplay ? 20 : 12}
              p={bigDisplay ? 1 : 0.2}
              bgcolor={themeColors.lightBlue}
            >
              {permissionsGroup
                .replace('_FE', '')
                .replace('can_access_', '')
                .replaceAll('_', ' ')}
            </Box>
          );
        })}
      </Box>
      {children}
      <Overlay visibility={showOverlay ? 'visible' : 'hidden'} />
    </Box>
  );
};
